import { useTranslation } from 'react-i18next';
import { purposeText, tripDates } from './purpose-text';
import type { ReportPurposeFields } from './types';

/**
 * The purpose of a note, its type and, for a Trasferta, its dates and scope (wave 2, R-a). A note
 * made before purposes existed reads «Senza motivazione» and keeps working.
 */
export function ReportPurpose({
  report,
  className,
}: {
  readonly report: ReportPurposeFields;
  readonly className?: string;
}) {
  const { t } = useTranslation('expenses');
  const purpose = purposeText(report);
  const dates = tripDates(report);
  const parts = [
    report.noteType ? t(`reports.noteType.${report.noteType}`) : null,
    dates,
    report.tripScope ? t(`reports.tripScope.${report.tripScope}`) : null,
  ].filter((part): part is string => part !== null);
  return (
    <span className={className} data-testid="report-purpose">
      <span className={purpose === null ? 'text-muted-foreground' : undefined}>
        {purpose ?? t('reports.noPurpose')}
      </span>
      {parts.length > 0 ? (
        <span className="text-muted-foreground"> · {parts.join(' · ')}</span>
      ) : null}
    </span>
  );
}

/** The report page's meta block: one labelled line each for the purpose, the type, the dates, the place. */
export function ReportPurposeLines({ report }: { readonly report: ReportPurposeFields }) {
  const { t } = useTranslation('expenses');
  const purpose = purposeText(report);
  const rows: readonly (readonly [string, string, boolean])[] = [
    [t('reports.page.purpose'), purpose ?? t('reports.noPurpose'), purpose === null],
    ...(report.noteType
      ? [[t('reports.page.type'), t(`reports.noteType.${report.noteType}`), false] as const]
      : []),
    ...(tripDates(report)
      ? [[t('reports.page.tripPeriod'), tripDates(report) ?? '', false] as const]
      : []),
    ...(report.tripScope
      ? [[t('reports.page.where'), t(`reports.tripScope.${report.tripScope}`), false] as const]
      : []),
  ];
  return (
    <dl
      className="screen-summary m-0 grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5"
      data-testid="report-purpose"
    >
      {rows.map(([label, value, muted]) => (
        <div key={label} className="col-span-2 grid grid-cols-subgrid">
          <dt className="text-muted-foreground">{label}</dt>
          <dd className={muted ? 'text-muted-foreground m-0' : 'm-0'}>{value}</dd>
        </div>
      ))}
    </dl>
  );
}
